import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import APICall, { dispatchClaimsAPICall } from "./apiCall.js"
import { getPanelToken } from "./auth/AuthActions.js"
import { loadRecentEvents } from "./eventsSlice.js";
import { panelsFetch } from "./panelsSlice.js";
import dayjs from "dayjs";

const siteEntityAdapter = createEntityAdapter({ selectId: ({ hwaddr }) => hwaddr });

const siteSelectors = siteEntityAdapter.getSelectors(state => state.sites );

export const sitesSlice = createSlice({
    name: "sites",
    initialState: siteEntityAdapter.getInitialState(),
    reducers: {
        siteAdded: siteEntityAdapter.addOne,
        sitesSet: siteEntityAdapter.setMany,

        clear: siteEntityAdapter.removeAll,

        siteUpdateName: {
            prepare(hwaddr, name) {
                return { payload: { hwaddr, name } };
            },
            reducer(state, action){
                siteEntityAdapter.updateOne(state, {
                    id: action.payload.hwaddr,
                    changes: {
                        name: action.payload.name
                    }
                })
            }
        },

        siteUpdateEmailEvents: {
            prepare(hwaddr, enable) {
                return { payload: { hwaddr, enable } };
            },
            reducer(state, action){
                siteEntityAdapter.updateOne(state, {
                    id: action.payload.hwaddr,
                    changes: {
                        emailEvents: action.payload.enable
                    }
                })
            }
        }, 
        siteResetEventCount: {
            prepare(hwaddr, networkId) {
                return { payload: { hwaddr, networkId } };
            },
            reducer(state, action){
                const current = siteSelectors.selectById(state, action.payload.hwaddr);
                siteEntityAdapter.updateOne(state, {
                    id: action.payload.hwaddr,
                    changes: {
                        lastEventSeen: (new Date).toISOString(),
                        unreadCount: current.unreadCount - current.perPanelCount[action.payload.networkId],
                        perPanelCount: { [action.payload.networkId]: 0 }
                    }
                })
            }
        }
    }
});

// Actions 
export const sitesFetch = () =>  async dispatch => {
    await dispatch(getPanelToken());
    const sites = await APICall("/v2/sites", null, null, "claims");
    return dispatch(sitesSlice.actions.sitesSet(sites))
};

export const siteCreate = (hwaddr, key, nickname, notify) => async dispatch => {
    await APICall("/v2/claims/create", { hwaddr, key, nickname, notify }, {}, "session");
    await dispatch(getPanelToken(true));
    await dispatch(sitesFetch());
};

export const siteRemove = (hwaddr) => async dispatch => {
    await APICall("/v2/claims/remove", { hwaddr }, {}, "session");
    await dispatch(getPanelToken(true));
    await dispatch(sitesFetch());
};

export const siteEmailEvents = (hwaddr, state) => dispatch => {
    dispatch(getPanelToken()).then(() => APICall("/v2/claims/email/" + hwaddr, { state: state }, null, "session"))
    dispatch(sitesSlice.actions.siteUpdateEmailEvents(hwaddr, state))
};
export const sitePushEvents = (hwaddr, state) => dispatch => {
    dispatch(getPanelToken()).then(() => APICall("/v2/claims/device/" + hwaddr, { state: state }, null, "session"))
};

export const siteUpdateName = (hwaddr, name ) => async dispatch => {
    await dispatch(dispatchClaimsAPICall(`/v2/sites/${hwaddr}/name`, { name }));
    dispatch(sitesSlice.actions.siteUpdateName(hwaddr, name));
}

export const siteResetEventCount = (hwaddr, networkId) => async dispatch => {
    await dispatch(getPanelToken()).then(() => APICall(`/v2/sites/${hwaddr}/resetEvent`, {}, null, "claims"));
    dispatch(
        sitesSlice.actions.siteResetEventCount(hwaddr, networkId)
    );
}

// Selectors

export function selectSiteName(state, hwaddr) {
    const site = siteSelectors.selectById(state, hwaddr);

    return site ? site.name : hwaddr.replace(/([0-9a-f]{2})(?!$)/g, "$1:");
}

export function selectSiteNameMap(state)
{
    return siteSelectors.selectAll(state).reduce( (o, a) => {
        o[a.hwaddr] = a.name;
        return o;
    }, {});
}

export const selectAllSites = siteSelectors.selectAll;

export const selectSiteByHwaddr = siteSelectors.selectById;

export const selectEmailEvents = (state, hwaddr) => siteSelectors.selectById(state, hwaddr).emailEvents;
export const selectPushEvents = (state, hwaddr) => siteSelectors.selectById(state, hwaddr).pushEvents;

export const selectPanelEventCount = (hwaddr, panelId) => state => siteSelectors.selectById(state, hwaddr).perPanelCount[panelId];
export const selectUnreadCount = (hwaddr) => state => {
    const site = siteSelectors.selectById(state, hwaddr);
    if(site == null){
        return 0;
    }
    return site.unreadCount;
}
export const selectLastEventSeen = hwaddr => state => dayjs(siteSelectors.selectById(state, hwaddr).lastEventSeen);
// hooks

export const useSite = hwaddr => {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(sitesFetch());
        dispatch(panelsFetch(hwaddr));
        dispatch(loadRecentEvents(hwaddr));
    }, [hwaddr]);
    return useSelector(state => selectSiteByHwaddr(state, hwaddr))
}

export const useSiteFromRouter = () => useHwaddrFromRouter(useSite);

export const usePanelEventCount = ( hwaddr, panelId) => useSelector(selectPanelEventCount(hwaddr, panelId))
export const useUnreadCount = ( hwaddr) => useSelector(selectUnreadCount(hwaddr))

export const useLastEventSeen = hwaddr => useSelector(selectLastEventSeen(hwaddr));

export const useHwaddrFromRouter = hook => {
    const { hwaddr } = useParams();
    return hook(hwaddr);
}


export default sitesSlice.reducer;

